import { useEffect, useState } from "react"
import { WindowScroller, AutoSizer, List, Grid } from "react-virtualized"

import {
	useProducts,
	useProductsFilters,
	DISPLAY_FILTER,
	GRID_DISPLAY,
	LIST_DISPLAY,
} from "lib/products"
import { listComponent } from "lib/tools/categories"
import debounce from "lib/tools/debounce"

import Film from "./list/film"

const FilmsList = ({ navRef, listRef, sectionPositions, setSectionPositions, setCurrentAlpha }) => {

	const sectionHeight = 94
	const subsectionHeight = 43
	const productHeight = 50
	const productGridHeight = 305

	const {
		displayMode,
		catalog: baseCatalog,
	} = useProducts()

	const { updateFilters } = useProductsFilters()

	const [gridRowsCount, setGridRowsCount] = useState(0)
	const [gridWidth, setGridWidth] = useState(null)
	const [elementPerRow, setElementPerRow] = useState(3)

	const catalog = displayMode === LIST_DISPLAY
		? baseCatalog.filter(row => !row.empty)
		: baseCatalog

	const manageRowsCount = (cat) => {
		if (gridRowsCount > 0) {
			return gridRowsCount
		}

		let count = 0, cellsCount = 0

		cat.forEach(item => {
			if (item.type == "section" || item.type == "subsection") {
				if (cellsCount > 0) {
					count += Math.ceil(cellsCount / 4)
				}
				count++
				cellsCount = 0
			} else if (!item.empty) {
				cellsCount++
			}
		})

		if (cellsCount > 0) {
			count += Math.round(cellsCount / 4)
		}

		setGridRowsCount(count)

		return count
	}

	const manageSectionPositions = () => {
		let positions = []
		let position = 0
		let cellIndex = 0
		let productIndex = 0
		let tPosition = null

		const grid = listRef.current.Grid || listRef.current

		catalog.forEach((target, index) => {

			if (target.type === "section" || target.type === "subsection") {
				// Count products total height
				if (productIndex > 0) {
					if (displayMode === LIST_DISPLAY) {
						position += cellIndex * rowHeight({ index: productIndex })
					} else {
						position += Math.floor(cellIndex / grid.props.columnCount) * productGridHeight
					}
				}

				if (target.type === "section") {
					// Push current section position
					tPosition = { index, target: target.title, top: position }
					positions = [...positions, tPosition]
				}

				// Add current section height
				position += rowHeight({ index })

				// Reset product count
				cellIndex = 0

			} else {
				// Add product count
				cellIndex++

				// Set first product to get height
				if (productIndex === 0) {
					productIndex = index
				}
			}
		})

		setSectionPositions(positions)
	}

	const renderRow = ({ index, style, key }) => {
		let target = catalog[index]

		return (
			<div key={key} style={style}>
				{getComponent(target, index)}
			</div>
		)
	}

	const getComponent = (target, index) => {
		switch (target.type) {
			case "product":
				return (
					<Film
						film={target.product}
						style={displayMode === GRID_DISPLAY ? { width: "auto" } : {}}
					/>
				)

			case "section":
				return (
					<div id={`results-title-${target.title}`} className="results-title">
						<h2>{target.title}</h2>
					</div>
				)

			case "subsection":
				return (
					<div
						className="results-sub-title title-alt"
						style={catalog[index - 1].type === "section"
							? {}
							: { marginTop: 20 }
						}
					>
						<h3 className="title-alt">
							{listComponent(
								{ name: `${target.title.charAt(0).toUpperCase()}${target.title.slice(1)}` },
								true
							)}
						</h3>
					</div>
				)
		}
	}

	const rowHeight = ({ index }) => {
		let height = productHeight
		const target = catalog[index]

		switch (target.type) {
			case "section":
				height = sectionHeight

				break

			case "subsection":
				if (catalog[index - 1] && catalog[index - 1].type === "section") {
					height = subsectionHeight
				} else {
					height = 63
				}

				break
		}

		return height
	}

	const renderCell = ({ style, columnIndex, rowIndex, key }) => {
		let index = (rowIndex * 4) + columnIndex
		let target = catalog[index]
		let customStyle = {}

		if (target.type == "section" || target.type == "subsection") {
			customStyle = { width: "100%" }
		} else if (target.empty) {
			return null
		}

		return (
			<div
				key={key}
				style={{ ...style, ...customStyle }}
			>
				{getComponent(target, index)}
			</div>
		)
	}

	const gridRowHeight = (width) => ({ index }) => {

		let target = catalog[index * elementPerRow]

		if (!target) return gridRowHeight(width)({ index: index = 1 })

		if (target.type === "section" || target.type === "subsection") {
			return rowHeight({ index: index * elementPerRow })
		}

		return productGridHeight
	}

	const handleScroll = debounce(
		scrollPos => {
			const navBoundingClientRect = navRef.current.getBoundingClientRect()
			const scrollTop = scrollPos.scrollTop > 0
				? scrollPos.scrollTop + (navBoundingClientRect.top + navBoundingClientRect.height) * 2 + sectionHeight
				: scrollPos.scrollTop

			sectionPositions.some((section, index) => {
				if (
					section.top < scrollTop
					&& (
						!sectionPositions[index + 1]
						|| scrollTop < sectionPositions[index + 1].top
					)
				) {
					setCurrentAlpha(section.target)
					return true
				}

				return false
			})
		},
		200
	)

	useEffect(() => {
		if (sectionPositions.length) {
			const handleResize = () => {
				window.scroll(0, 0)
				setCurrentAlpha(sectionPositions[0].target)
			}

			handleResize()

			window.addEventListener('resize', handleResize)

			return () => {
				window.removeEventListener('resize', handleResize)
			}
		}
	}, [sectionPositions])

	useEffect(() => {
		if (displayMode === LIST_DISPLAY) {
			listRef.current.forceUpdateGrid()
		} else {
			setGridRowsCount(manageRowsCount(baseCatalog))
			listRef.current.recomputeGridSize()
		}

		manageSectionPositions()
	}, [baseCatalog, displayMode, elementPerRow])

	useEffect(() => {
		if (!gridWidth) {
			return
		}

		if (gridWidth >= 1150) {
			setElementPerRow(4)
		}

		if (gridWidth < 1150 && gridWidth >= 1000) {
			setElementPerRow(3)
		}

		if (gridWidth < 1000) {
			updateFilters(DISPLAY_FILTER, LIST_DISPLAY)
		}
	}, [gridWidth])

	if (displayMode === LIST_DISPLAY) {
		return (
			<WindowScroller onScroll={handleScroll}>
				{({ height, width, registerChild, isScrolling, onChildScroll, scrollTop }) => (
					<AutoSizer disableHeight>
						{({ width }) => (
							<div ref={registerChild}>
								<List
									ref={listRef}
									className={`results grid films archive list`}
									autoHeight
									overscanRowCount={20}
									rowCount={catalog.length}
									rowHeight={rowHeight}
									rowRenderer={renderRow}
									height={height + 200}
									width={width}
									isScrolling={isScrolling}
									onScroll={onChildScroll}
									scrollTop={scrollTop}
								/>
							</div>
						)}
					</AutoSizer>
				)}
			</WindowScroller>
		)
	}

	return (
		<WindowScroller onScroll={handleScroll}>
			{({ height, width, registerChild, isScrolling, onChildScroll, scrollTop }) => (
				<AutoSizer disableHeight>
					{({ width }) => {
						setTimeout(() => {
							setGridWidth(width)
						}, 1000)

						return (
							<div ref={registerChild}>
								<Grid
									ref={listRef}
									className={`results grid films archive`}
									cellRenderer={renderCell}
									columnWidth={width / elementPerRow}
									columnCount={elementPerRow}
									overscanColumnCount={0}
									overscanRowCount={0}
									rowHeight={gridRowHeight(width)}
									rowCount={manageRowsCount(baseCatalog)}
									autoHeight
									height={height + 200}
									width={width}
									isScrolling={isScrolling}
									onScroll={onChildScroll}
									scrollTop={scrollTop}
								/>
							</div>
						)
					}}
				</AutoSizer>
			)}
		</WindowScroller>
	)
}

export default FilmsList
